/**
 * Geofence Tab Component
 * Displays geofence list, drawing controls, and devices within geofences
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  List,
  ListItem,
  ListItemSecondaryAction,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Alert,
  Paper,
  CircularProgress,
  Collapse,
  Tooltip,
  useTheme
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  VisibilityOff as VisibilityOffIcon,
  Place as GeofenceIcon,
  Shield as ShieldIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  FactCheck as FactCheckIcon,
} from '@mui/icons-material';
import { geofenceService, Geofence, DeviceInGeofence } from '@signals/app-shell/services/geofenceService';
import { useParticipantLabels } from '@signals/app-shell/hooks/useParticipantLabels';
import PromoteToPacketDialog, { PromoteTarget } from '@signals/app-shell/components/intel/PromoteToPacketDialog';
import { useFeatureFlagsStore } from '@signals/app-shell/stores/featureFlagsStore';
import ForceProtectionDialog from './ForceProtectionDialog';

// DFP alert shape mirrors modules/PathfinderRF/FE/src/services/dfpAlertsService.ts DFPAlert.
// Kept local to avoid cross-module import (RF module is optional).
export interface GFDFPAlert {
  id: number;
  device_id: string;
  anomaly_type: string;
  signal_type?: string;
  severity?: string;
  confidence_score?: number | null;
  latitude?: number | null;
  longitude?: number | null;
  detected_at: string;
  evidence?: Record<string, any>;
}

// Keep in sync with PathfinderRF ANOMALY_TYPE_LABELS for DFP types shown here.
const GEOFENCE_ANOMALY_LABELS: Record<string, string> = {
  imsi_catcher_suspect: 'Rogue Base Station',
  rogue_access_point: 'Rogue AP',
  personal_hotspot: 'Personal Hotspot',
  evil_twin: 'Evil Twin',
  cotraveler: 'Co-Traveler',
  drone_detected: 'Drone',
  unknown_device: 'Unknown Device',
  mac_anomaly: 'MAC Anomaly',
  gnss_spoofing: 'GNSS Spoofing',
  jamming_gnss: 'GNSS Jamming',
  ble_located: 'BLE Located',
  wifi_located: 'WiFi Located',
  cell_located: 'Cell Located',
  watchlist_hit: 'Watchlist Hit',
  ble_flood: 'BLE Flood',
  watchlist_proximity: 'Watchlist Proximity',
};

function geofenceAnomalyLabel(type: string): string {
  if (!type) return 'Unknown';
  return GEOFENCE_ANOMALY_LABELS[type]
    || type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

interface GeofenceDuration {
  geofence_id: number;
  geofence_name: string;
  device_id: string;
  entity_id?: string;
  enter_time: string;
  exit_time?: string;
  duration_seconds?: number;
  duration_formatted?: string;
  currently_inside: boolean;
}

interface MapBounds {
  north: number;
  south: number;
  east: number;
  west: number;
}

const MAP_EXTENT_FILTER_PADDING_RATIO = 0.03;

const normalizeLongitudeNear = (lng: number, targetLng: number): number => (
  lng + (Math.round((targetLng - lng) / 360) * 360)
);

interface GeofenceTabProps {
  onStartDrawing: () => void;
  onStopDrawing: () => void;
  isDrawing: boolean;
  onGeofenceSelect: (geofenceId: number | null) => void;
  selectedGeofenceId: number | null;
  onGeofenceDrawn?: any;
  geofences: Geofence[];
  mapBounds?: MapBounds | null;
  onClearPendingGeofence?: () => void;
  onGeofenceCreated?: () => void;
  onGeofenceDeleted?: () => void;
  onGeofenceZoom?: (lat: number, lng: number) => void;
  /** DFP signal-anomaly alerts; when provided, each geofence row shows its threat summary. */
  dfpAlerts?: GFDFPAlert[];
  /**
   * Look-back window (in hours) used by the Geofence Activity History modal.
   * Sourced from the COP top-bar date dropdown so the modal matches the
   * timeline below it instead of using a hard-coded 7-day window.
   */
  activityHoursWindow?: number;
  /** Human-readable label of the current window, e.g. "Last Hour". */
  activityWindowLabel?: string;
}

const geofenceIntersectsBounds = (geofence: Geofence, bounds: MapBounds | null | undefined): boolean => {
  if (!bounds) return true;

  let geometry: Geofence['geometry'] | undefined;
  const raw = geofence.geometry as Geofence['geometry'] | string | undefined;
  if (typeof raw === 'string') {
    try {
      geometry = JSON.parse(raw) as Geofence['geometry'];
    } catch {
      return true;
    }
  } else {
    geometry = raw;
  }

  const coordinates = geometry?.coordinates?.[0];
  if (!coordinates || coordinates.length === 0) {
    // If geometry is missing/invalid, still show in list (don't hide due to bounds filter)
    return true;
  }

  const rawLngSpan = bounds.east >= bounds.west
    ? bounds.east - bounds.west
    : (bounds.east + 360) - bounds.west;
  const lngSpan = rawLngSpan > 0 ? rawLngSpan : 360;
  const latSpan = Math.max(bounds.north - bounds.south, 0);
  const lngPadding = lngSpan * MAP_EXTENT_FILTER_PADDING_RATIO;
  const latPadding = latSpan * MAP_EXTENT_FILTER_PADDING_RATIO;
  const paddedWest = bounds.west - lngPadding;
  const paddedEast = (bounds.east >= bounds.west ? bounds.east : bounds.east + 360) + lngPadding;
  const paddedSouth = bounds.south - latPadding;
  const paddedNorth = bounds.north + latPadding;
  const boundsCenterLng = paddedWest + ((paddedEast - paddedWest) / 2);

  let minLng = Infinity;
  let maxLng = -Infinity;
  let minLat = Infinity;
  let maxLat = -Infinity;

  coordinates.forEach((coord) => {
    const [lng, lat] = coord;
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) return;

    const normalizedLng = normalizeLongitudeNear(lng, boundsCenterLng);
    minLng = Math.min(minLng, normalizedLng);
    maxLng = Math.max(maxLng, normalizedLng);
    minLat = Math.min(minLat, lat);
    maxLat = Math.max(maxLat, lat);
  });

  if (![minLng, maxLng, minLat, maxLat].every(Number.isFinite)) return false;

  const latOverlaps = maxLat >= paddedSouth && minLat <= paddedNorth;
  const lngOverlaps = maxLng >= paddedWest && minLng <= paddedEast;

  return latOverlaps && lngOverlaps;
};

const GeofenceTab: React.FC<GeofenceTabProps> = ({
  onStartDrawing,
  onStopDrawing,
  isDrawing,
  onGeofenceSelect,
  selectedGeofenceId,
  onGeofenceDrawn,
  geofences,
  mapBounds,
  onClearPendingGeofence,
  onGeofenceCreated,
  onGeofenceDeleted,
  onGeofenceZoom,
  dfpAlerts,
  activityHoursWindow = 1,
  activityWindowLabel = 'Last Hour',
}) => {
  const { singular } = useParticipantLabels();
  const intelEnabled = useFeatureFlagsStore((s) => s.intel);
  const [promoteTarget, setPromoteTarget] = useState<PromoteTarget | null>(null);
  const [promoteExtras, setPromoteExtras] = useState<PromoteTarget[]>([]);
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const primaryColor = theme.palette.primary.main;
  
  const hexToRgb = (hex: string) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}` : '76, 175, 80';
  };
  const primaryRgb = hexToRgb(primaryColor);

  const colors = {
    cardBg: isDark
      ? 'linear-gradient(135deg, rgba(30, 32, 40, 0.98), rgba(24, 26, 32, 0.99))'
      : 'linear-gradient(135deg, rgba(255, 255, 255, 0.98), rgba(245, 247, 250, 0.95))',
    border: isDark ? `rgba(${primaryRgb}, 0.25)` : `rgba(${primaryRgb}, 0.3)`,
    headerBg: isDark ? `rgba(${primaryRgb}, 0.05)` : `rgba(${primaryRgb}, 0.05)`,
    text: isDark ? 'white' : '#1a1a2e',
    textSecondary: isDark ? '#9e9e9e' : '#666',
    textTertiary: isDark ? '#757575' : '#999',
    subtleBg: isDark ? `rgba(${primaryRgb}, 0.05)` : `rgba(${primaryRgb}, 0.05)`,
  };

  const [error, setError] = useState<string | null>(null);
  const [nameDialogOpen, setNameDialogOpen] = useState(false);
  const [newGeofenceName, setNewGeofenceName] = useState('');
  const [newGeofenceDescription, setNewGeofenceDescription] = useState('');
  const [pendingGeofenceGeoJSON, setPendingGeofenceGeoJSON] = useState<any>(null);
  const [isCreatingGeofence, setIsCreatingGeofence] = useState(false);
  const [devicesInGeofences, setDevicesInGeofences] = useState<Record<number, DeviceInGeofence[]>>({});
  const [geofenceDurations, setGeofenceDurations] = useState<GeofenceDuration[]>([]);
  const [selectedGeofenceActivity, setSelectedGeofenceActivity] = useState<GeofenceDuration[] | null>(null);
  const [activityDialogOpen, setActivityDialogOpen] = useState(false);
  const [deletedGeofenceIds, setDeletedGeofenceIds] = useState<Set<number>>(new Set());
  const [expandedThreatsId, setExpandedThreatsId] = useState<number | null>(null);
  const [fpDialogGeofence, setFpDialogGeofence] = useState<Geofence | null>(null);

  const processedGeofenceRef = useRef<any>(null);
  // Guards against overlapping loadDevicesInGeofences calls when the parent
  // re-renders rapidly (SSE updates etc.). Without this the effect fired on
  // every render and produced thousands of "Failed to fetch" errors when the
  // backend was unreachable.
  const loadingDevicesRef = useRef(false);
  const lastDevicesErrorAtRef = useRef<number>(0);

  // Filter out deleted geofences from display. Memoized so the reference is
  // stable across renders when neither `geofences` nor `deletedGeofenceIds`
  // changed; otherwise downstream callbacks/effects re-fire on every render.
  const displayGeofences = useMemo(
    () => geofences.filter(g => !deletedGeofenceIds.has(g.id) && geofenceIntersectsBounds(g, mapBounds)),
    [geofences, deletedGeofenceIds, mapBounds]
  );

  // Point-in-polygon helper for matching DFP alerts to geofences client-side.
  const pointInPolygon = useCallback((lat: number, lon: number, coords: number[][]): boolean => {
    let inside = false;
    for (let i = 0, j = coords.length - 1; i < coords.length; j = i++) {
      const xi = coords[i][1], yi = coords[i][0];
      const xj = coords[j][1], yj = coords[j][0];
      if (((yi > lon) !== (yj > lon)) && (lat < (xj - xi) * (lon - yi) / (yj - yi) + xi)) {
        inside = !inside;
      }
    }
    return inside;
  }, []);

  const alertsByGeofence = useMemo(() => {
    const map: Record<number, GFDFPAlert[]> = {};
    const alerts: GFDFPAlert[] = dfpAlerts ?? [];
    for (const gf of displayGeofences) {
      const coords = (gf as any).geometry?.coordinates?.[0];
      if (!coords || coords.length < 3) { map[gf.id] = []; continue; }
      map[gf.id] = alerts.filter((a: GFDFPAlert) => {
        const lat = a.latitude;
        const lon = a.longitude;
        return lat != null && lon != null && pointInPolygon(lat, lon, coords);
      });
    }
    return map;
  }, [displayGeofences, dfpAlerts, pointInPolygon]);
  
  // Clear deleted IDs when parent refreshes with new data (sync is complete)
  useEffect(() => {
    if (deletedGeofenceIds.size > 0) {
      // Check if all deleted IDs are now gone from parent data
      const allDeletedGone = Array.from(deletedGeofenceIds).every(
        id => !geofences.some(g => g.id === id)
      );
      if (allDeletedGone) {
        setDeletedGeofenceIds(new Set());
      }
    }
  }, [geofences, deletedGeofenceIds]);

  const loadDevicesInGeofences = useCallback(async () => {
    if (displayGeofences.length === 0) return;
    // Skip if a previous batch is still in-flight to avoid overlapping bursts
    // when the parent re-renders (e.g. SSE / polling updates).
    if (loadingDevicesRef.current) return;
    loadingDevicesRef.current = true;
    try {
      const results = await Promise.allSettled(
        displayGeofences.map(gf =>
          geofenceService
            .getDevicesInGeofence(gf.id)
            .then(devices => ({ id: gf.id, devices }))
        )
      );

      const devicesMap: Record<number, DeviceInGeofence[]> = {};
      let firstError: unknown = null;
      results.forEach(r => {
        if (r.status === 'fulfilled') {
          devicesMap[r.value.id] = r.value.devices;
        } else if (!firstError) {
          firstError = r.reason;
        }
      });
      setDevicesInGeofences(prev => ({ ...prev, ...devicesMap }));

      if (firstError) {
        // Throttle error logging so a flapping backend doesn't flood the
        // console — at most one log per 10s.
        const now = Date.now();
        if (now - lastDevicesErrorAtRef.current > 10_000) {
          lastDevicesErrorAtRef.current = now;
          console.error('[GeofenceTab] Error loading devices:', firstError);
        }
      }
    } finally {
      loadingDevicesRef.current = false;
    }
  }, [displayGeofences]);

  const loadGeofenceDurations = useCallback(async () => {    
    try {
      const baseUrl = process.env.REACT_APP_API_URL || '/api';
      const url = `${baseUrl}/geofences/durations?hours=24`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token') || ''}`
        }
      });
      
      if (response.ok) {
        const durations = await response.json();
        setGeofenceDurations(durations);
      } else {
        setGeofenceDurations([]);
      }
    } catch (err: any) {
      setGeofenceDurations([]);
    }
  }, []);

  // Keep the latest callbacks in refs so the polling effect below is driven
  // purely by `displayGeofences.length` (a primitive). Listing the callbacks
  // directly causes the effect to re-fire on every parent render because the
  // callback identities change with `displayGeofences`.
  const loadDevicesRef = useRef(loadDevicesInGeofences);
  const loadDurationsRef = useRef(loadGeofenceDurations);
  useEffect(() => {
    loadDevicesRef.current = loadDevicesInGeofences;
    loadDurationsRef.current = loadGeofenceDurations;
  }, [loadDevicesInGeofences, loadGeofenceDurations]);

  useEffect(() => {
    if (displayGeofences.length === 0) return;

    loadDevicesRef.current();
    loadDurationsRef.current();

    const interval = setInterval(() => {
      loadDevicesRef.current();
      loadDurationsRef.current();
    }, 30000);

    return () => clearInterval(interval);
  }, [displayGeofences.length]);

  const handleGeofenceDrawn = useCallback((geojson: any) => {
    setPendingGeofenceGeoJSON(geojson);
    setNameDialogOpen(true);
  }, []);

  useEffect(() => {
    if (onGeofenceDrawn && onGeofenceDrawn !== processedGeofenceRef.current) {
      processedGeofenceRef.current = onGeofenceDrawn;
      handleGeofenceDrawn(onGeofenceDrawn);
      if (onClearPendingGeofence) onClearPendingGeofence();
    }
  }, [onGeofenceDrawn, onClearPendingGeofence, handleGeofenceDrawn]);

  const handleStartDrawing = () => onStartDrawing();

  const handleCancelDrawing = () => {
    if (isCreatingGeofence) return;

    setNameDialogOpen(false);
    setNewGeofenceName('');
    setNewGeofenceDescription('');
    setPendingGeofenceGeoJSON(null);
    onStopDrawing();
  };

  const handleSaveGeofence = async () => {
    if (isCreatingGeofence) return;

    if (!newGeofenceName.trim()) {
      setError('Geofence name is required');
      return;
    }

    if (!pendingGeofenceGeoJSON || !pendingGeofenceGeoJSON.geometry) {
      setError('Invalid geofence data');
      return;
    }

    setIsCreatingGeofence(true);

    try {
      await geofenceService.createGeofence({
        name: newGeofenceName.trim(),
        description: newGeofenceDescription.trim() || undefined,
        geometry: pendingGeofenceGeoJSON.geometry
      });

      setNameDialogOpen(false);
      setNewGeofenceName('');
      setNewGeofenceDescription('');
      setPendingGeofenceGeoJSON(null);
      onStopDrawing();
      setError(null);
      
      if (onGeofenceCreated) onGeofenceCreated();
    } catch (err: any) {
      setError(err.message || 'Failed to create geofence');
    } finally {
      setIsCreatingGeofence(false);
    }
  };

  const handleDeleteGeofence = async (geofenceId: number) => {
    if (!window.confirm('Delete this geofence?')) return;

    try {
      await geofenceService.deleteGeofence(geofenceId);
      
      // Immediately hide from UI
      setDeletedGeofenceIds(prev => {
        const updated = new Set(prev);
        updated.add(geofenceId);
        return updated;
      });
      
      // Update local state
      setDevicesInGeofences(prev => {
        const updated = { ...prev };
        delete updated[geofenceId];
        return updated;
      });
      
      if (selectedGeofenceId === geofenceId) {
        onGeofenceSelect(null);
      }
      
      // Trigger parent reload
      if (onGeofenceDeleted) onGeofenceDeleted();
      
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to delete geofence');
    }
  };

  const handleToggleGeofenceVisibility = (geofenceId: number) => {
    onGeofenceSelect(selectedGeofenceId === geofenceId ? null : geofenceId);
  };

  const getDeviceDuration = (geofenceId: number, deviceId: string) => {
    return geofenceDurations.find(d => 
      d.geofence_id === geofenceId && d.device_id === deviceId && d.currently_inside
    );
  };

  const getEntityId = (deviceId: string) => {
    const durationEntry = geofenceDurations.find(d => d.device_id === deviceId);
    if (durationEntry?.entity_id) return durationEntry.entity_id;
    
    for (const devices of Object.values(devicesInGeofences) as DeviceInGeofence[][]) {
      const device = devices.find(d => d.device_id === deviceId);
      if (device?.entity_id) return device.entity_id;
    }
    
    return `${singular} ${deviceId.substring(0, 4)}`;
  };

  const handleGeofenceClick = async (geofence: Geofence) => {
    // Select and zoom to geofence
    onGeofenceSelect(geofence.id);
    if (onGeofenceZoom && geofence.geometry?.coordinates) {
      const coords = geofence.geometry.coordinates[0];
      if (coords && coords.length > 0) {
        let sumLng = 0, sumLat = 0;
        coords.forEach((coord: number[]) => {
          sumLng += coord[0];
          sumLat += coord[1];
        });
        onGeofenceZoom(sumLat / coords.length, sumLng / coords.length);
      }
    }
    
    // Load activity history for this geofence using the COP top-bar date
    // window so the modal stays in sync with the dropdown and the bottom
    // Geofence Activity Timeline. Backend caps at 720h via Query(le=720).
    try {
      const baseUrl = process.env.REACT_APP_API_URL || '/api';
      const safeHours = Math.min(Math.max(activityHoursWindow || 24, 1), 720);
      const url = `${baseUrl}/geofences/durations?hours=${safeHours}&geofence_id=${geofence.id}`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token') || ''}`
        }
      });
      
      if (response.ok) {
        const activity = await response.json();
        setSelectedGeofenceActivity(activity);
        setActivityDialogOpen(true);
      }
    } catch (err) {
      console.error('Failed to load geofence activity:', err);
    }
  };

  return (
    <Box sx={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      {/* Header Card */}
      <Paper sx={{ 
        background: colors.cardBg,
        border: `1px solid ${colors.border}`,
        borderRadius: 3,
        overflow: 'hidden',
        backdropFilter: 'blur(10px)',
        boxShadow: isDark ? 'none' : '0 2px 12px rgba(0,0,0,0.08)',
        mb: 2
      }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          p: 2,
          borderBottom: `1px solid ${colors.border}`,
          background: colors.headerBg,
        }}>
          <Box sx={{
            width: 32,
            height: 32,
            borderRadius: 2,
            background: isDark 
              ? `linear-gradient(135deg, rgba(${primaryRgb}, 0.2), rgba(${primaryRgb}, 0.1))`
              : `linear-gradient(135deg, rgba(${primaryRgb}, 0.15), rgba(${primaryRgb}, 0.08))`,
            border: `1px solid ${colors.border}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <GeofenceIcon sx={{ color: primaryColor, fontSize: 18 }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ color: colors.text, fontWeight: 'bold', fontSize: '0.85rem' }}>
              Geofences
            </Typography>
            <Typography sx={{ color: colors.textTertiary, fontSize: '0.65rem' }}>
              {mapBounds ? `${displayGeofences.length} in view` : `${displayGeofences.length} configured`}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ p: 2 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleStartDrawing}
            disabled={isDrawing}
            fullWidth
            sx={{ 
              background: `linear-gradient(135deg, ${primaryColor}, ${primaryColor}dd)`,
              '&:hover': {
                background: `linear-gradient(135deg, ${primaryColor}dd, ${primaryColor}bb)`,
              }
            }}
          >
            {isDrawing ? 'Drawing...' : 'Draw New Geofence'}
          </Button>
          {isDrawing && (
            <Alert 
              severity="info" 
              sx={{ 
                mt: 1.5,
                backgroundColor: 'rgba(33, 150, 243, 0.1)',
                color: colors.text,
                border: '1px solid rgba(33, 150, 243, 0.3)',
                borderRadius: 2,
                '& .MuiAlert-icon': { color: '#2196f3' }
              }}
            >
              Click on map to draw. Double-click to finish.
            </Alert>
          )}
        </Box>
      </Paper>

      {/* Error Display */}
      {error && (
        <Alert 
          severity="error" 
          onClose={() => setError(null)} 
          sx={{ 
            mb: 2,
            backgroundColor: 'rgba(244, 67, 54, 0.1)',
            color: colors.text,
            border: '1px solid rgba(244, 67, 54, 0.3)',
            borderRadius: 2,
          }}
        >
          {error}
        </Alert>
      )}

      {/* Geofence List */}
      <Paper sx={{ 
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        background: colors.cardBg,
        border: `1px solid ${colors.border}`,
        borderRadius: 3,
        overflow: 'hidden',
        backdropFilter: 'blur(10px)',
        boxShadow: isDark ? 'none' : '0 2px 12px rgba(0,0,0,0.08)',
      }}>
        <List sx={{ 
          flex: 1, 
          minHeight: 0,
          overflowY: 'auto', 
          p: 1,
          '&::-webkit-scrollbar': { width: 4 },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': {
            background: isDark ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.15)',
            borderRadius: 4,
          },
        }}>
          {displayGeofences.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <GeofenceIcon sx={{ fontSize: 48, color: colors.textTertiary, mb: 1 }} />
              <Typography sx={{ color: colors.textSecondary, fontSize: '0.85rem' }}>
                {mapBounds && geofences.length > 0 ? 'No geofences in current map view' : 'No geofences created yet'}
              </Typography>
              <Typography sx={{ color: colors.textTertiary, fontSize: '0.75rem' }}>
                Draw a geofence on the map to get started
              </Typography>
            </Box>
          ) : (
            displayGeofences.map((geofence) => {
              const devicesInside = devicesInGeofences[geofence.id] || [];
              const isSelected = selectedGeofenceId === geofence.id;

              return (
                <ListItem
                  key={geofence.id}
                  onClick={() => handleGeofenceClick(geofence)}
                  sx={{
                    mb: 1,
                    borderRadius: 2,
                    border: isSelected ? `2px solid ${primaryColor}` : `1px solid ${colors.border}`,
                    background: isSelected 
                      ? isDark ? `rgba(${primaryRgb}, 0.15)` : `rgba(${primaryRgb}, 0.08)`
                      : isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': { 
                      background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                      transform: 'translateX(2px)',
                    },
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    p: 1.5,
                  }}
                >
                  <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, pr: intelEnabled ? '128px' : '96px' }}>
                      <Typography sx={{ color: colors.text, fontWeight: 600, fontSize: '0.85rem' }}>
                        {geofence.name}
                      </Typography>
                      <Chip
                        label={`${devicesInside.length} inside`}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: '0.65rem',
                          fontWeight: 600,
                          backgroundColor: devicesInside.length > 0 
                            ? 'rgba(76, 175, 80, 0.15)' 
                            : isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
                          color: devicesInside.length > 0 ? '#4caf50' : colors.textSecondary,
                          border: devicesInside.length > 0 
                            ? '1px solid rgba(76, 175, 80, 0.3)' 
                            : `1px solid ${colors.border}`,
                        }}
                      />
                    </Box>
                    <Typography sx={{ color: colors.textTertiary, fontSize: '0.7rem', mb: geofence.description ? 0.5 : 1 }}>
                      Created: {new Date(geofence.created_at).toLocaleDateString()}
                    </Typography>
                    {geofence.description && (
                      <Typography sx={{ 
                        color: colors.textSecondary, 
                        fontSize: '0.72rem', 
                        mb: 1,
                        lineHeight: 1.4,
                        fontStyle: 'italic',
                        maxHeight: 40,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                      }}>
                        {geofence.description}
                      </Typography>
                    )}
                    {devicesInside.length > 0 && (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {devicesInside.slice(0, 3).map((device) => {
                          const entityId = getEntityId(device.device_id);
                          const duration = getDeviceDuration(geofence.id, device.device_id);
                          
                          return (
                            <Chip
                              key={device.device_id}
                              label={`${entityId} ${duration?.duration_formatted ? `• ${duration.duration_formatted}` : ''}`}
                              size="small"
                              onClick={intelEnabled ? (e) => {
                                e.stopPropagation();
                                setPromoteExtras([{
                                  member_type: 'geofence',
                                  member_id: String(geofence.id),
                                  label: geofence.name,
                                }]);
                                setPromoteTarget({
                                  member_type: 'device',
                                  member_id: device.device_id,
                                  label: entityId,
                                  is_primary: true,
                                });
                              } : undefined}
                              sx={{ 
                                height: 22,
                                fontSize: '0.65rem',
                                backgroundColor: 'rgba(33, 150, 243, 0.1)',
                                color: '#64b5f6',
                                border: '1px solid rgba(33, 150, 243, 0.2)',
                                cursor: intelEnabled ? 'pointer' : 'default',
                              }}
                            />
                          );
                        })}
                        {devicesInside.length > 3 && (
                          <Chip
                            label={`+${devicesInside.length - 3}`}
                            size="small"
                            sx={{ 
                              height: 22,
                              fontSize: '0.65rem',
                              backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
                              color: colors.textSecondary,
                            }}
                          />
                        )}
                      </Box>
                    )}
                    {/* DFP Threat Summary (only renders when RF module is enabled and dfpAlerts are passed) */}
                    {(() => {
                      const gfAlerts = alertsByGeofence[geofence.id] || [];
                      const isThreatsOpen = expandedThreatsId === geofence.id;
                      if (gfAlerts.length === 0 && !isThreatsOpen) return null;
                      const confirmed = gfAlerts.filter(a => (a.confidence_score ?? 0) >= 0.75).length;
                      const probable = gfAlerts.filter(a => (a.confidence_score ?? 0) >= 0.50 && (a.confidence_score ?? 0) < 0.75).length;
                      const possible = gfAlerts.filter(a => (a.confidence_score ?? 0) >= 0.20 && (a.confidence_score ?? 0) < 0.50).length;
                      const byType: Record<string, number> = {};
                      gfAlerts.forEach(a => { byType[a.anomaly_type] = (byType[a.anomaly_type] || 0) + 1; });
                      return (
                        <Box sx={{ mt: 1, width: '100%' }}>
                          <Box
                            sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', py: 0.25 }}
                            onClick={(e) => { e.stopPropagation(); setExpandedThreatsId(isThreatsOpen ? null : geofence.id); }}
                          >
                            <ShieldIcon sx={{ fontSize: 14, color: confirmed > 0 ? '#f44336' : probable > 0 ? '#ff9800' : '#42a5f5' }} />
                            <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: confirmed > 0 ? '#f44336' : probable > 0 ? '#ff9800' : '#42a5f5' }}>
                              {gfAlerts.length} threat{gfAlerts.length !== 1 ? 's' : ''}
                            </Typography>
                            {confirmed > 0 && <Chip size="small" label={`${confirmed} confirmed`} sx={{ height: 16, fontSize: '0.55rem', fontWeight: 700, bgcolor: 'rgba(244,67,54,0.15)', color: '#f44336' }} />}
                            {probable > 0 && <Chip size="small" label={`${probable} probable`} sx={{ height: 16, fontSize: '0.55rem', fontWeight: 700, bgcolor: 'rgba(255,152,0,0.15)', color: '#ff9800' }} />}
                            {possible > 0 && <Chip size="small" label={`${possible} possible`} sx={{ height: 16, fontSize: '0.55rem', fontWeight: 700, bgcolor: 'rgba(66,165,245,0.12)', color: '#42a5f5' }} />}
                            <Box sx={{ flex: 1 }} />
                            {isThreatsOpen ? <ExpandLessIcon sx={{ fontSize: 14, opacity: 0.4 }} /> : <ExpandMoreIcon sx={{ fontSize: 14, opacity: 0.4 }} />}
                          </Box>
                          <Collapse in={isThreatsOpen}>
                            <Box sx={{ mt: 0.5, borderTop: '1px dashed', borderColor: 'divider', pt: 0.5 }}>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 0.75 }}>
                                {Object.entries(byType).sort((a, b) => b[1] - a[1]).map(([type, count]) => (
                                  <Chip key={type} size="small" label={`${geofenceAnomalyLabel(type)} (${count})`}
                                    sx={{ height: 18, fontSize: '0.55rem', bgcolor: 'rgba(255,255,255,0.05)', border: '1px solid', borderColor: 'divider' }} />
                                ))}
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', px: 0.5, py: 0.25, opacity: 0.5 }}>
                                <Typography variant="caption" sx={{ width: 90, fontSize: '0.5rem', fontWeight: 700, textTransform: 'uppercase' }}>Type</Typography>
                                <Typography variant="caption" sx={{ flex: 1, fontSize: '0.5rem', fontWeight: 700, textTransform: 'uppercase' }}>Target</Typography>
                                <Typography variant="caption" sx={{ width: 36, fontSize: '0.5rem', fontWeight: 700, textTransform: 'uppercase', textAlign: 'right' }}>Conf</Typography>
                                <Typography variant="caption" sx={{ width: 30, fontSize: '0.5rem', fontWeight: 700, textTransform: 'uppercase', textAlign: 'right' }}>Age</Typography>
                              </Box>
                              {gfAlerts.slice(0, 8).map(a => {
                                const conf = Math.round((a.confidence_score ?? 0) * 100);
                                const sevColor = conf >= 75 ? '#f44336' : conf >= 50 ? '#ff9800' : '#42a5f5';
                                const ev = a.evidence || {};
                                const target = ev.address || ev.bssid || ev.external_id || a.device_id?.substring(0, 12) || '—';
                                const mins = Math.floor((Date.now() - new Date(a.detected_at).getTime()) / 60000);
                                const age = mins < 1 ? '<1m' : mins < 60 ? `${mins}m` : mins < 1440 ? `${Math.floor(mins / 60)}h` : `${Math.floor(mins / 1440)}d`;
                                return (
                                  <Box key={a.id} sx={{ display: 'flex', alignItems: 'center', px: 0.5, py: 0.3, borderBottom: '1px solid', borderColor: 'divider',
                                    '&:last-child': { borderBottom: 'none' } }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: 90 }}>
                                      <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: sevColor, flexShrink: 0 }} />
                                      <Typography variant="caption" noWrap sx={{ fontSize: '0.6rem', fontWeight: 600, color: sevColor }}>{geofenceAnomalyLabel(a.anomaly_type)}</Typography>
                                    </Box>
                                    <Typography variant="caption" noWrap sx={{ flex: 1, fontSize: '0.6rem', fontFamily: '"Roboto Mono", monospace', opacity: 0.8 }}>{target}</Typography>
                                    <Typography variant="caption" sx={{ width: 36, fontSize: '0.6rem', fontWeight: 700, color: sevColor, textAlign: 'right' }}>{conf}%</Typography>
                                    <Typography variant="caption" sx={{ width: 30, fontSize: '0.6rem', opacity: 0.6, textAlign: 'right' }}>{age}</Typography>
                                    {intelEnabled && (
                                      <Tooltip title="Add to Intel Packet">
                                        <IconButton
                                          size="small"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setPromoteExtras([{
                                              member_type: 'geofence',
                                              member_id: String(geofence.id),
                                              label: geofence.name,
                                            }]);
                                            setPromoteTarget({
                                              member_type: 'dfp_alert',
                                              member_id: String(a.id),
                                              label: `${geofenceAnomalyLabel(a.anomaly_type)} ${target}`.trim(),
                                              is_primary: true,
                                              meta: { device_id: a.device_id, geofence_id: geofence.id },
                                            });
                                          }}
                                          sx={{ p: 0.15, ml: 0.25 }}
                                        >
                                          <FactCheckIcon sx={{ fontSize: 13, color: '#ffb300' }} />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                  </Box>
                                );
                              })}
                              {gfAlerts.length > 8 && (
                                <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', py: 0.5, opacity: 0.5, fontSize: '0.6rem' }}>
                                  +{gfAlerts.length - 8} more
                                </Typography>
                              )}
                            </Box>
                          </Collapse>
                        </Box>
                      );
                    })()}
                  </Box>
                  <ListItemSecondaryAction sx={{ top: 16, right: 12, transform: 'none' }}>
                    {intelEnabled && (
                      <Tooltip title="Promote to Intel Packet">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setPromoteExtras([]);
                            setPromoteTarget({
                              member_type: 'geofence',
                              member_id: String(geofence.id),
                              label: geofence.name,
                              is_primary: true,
                            });
                          }}
                          sx={{ color: '#ffb300', '&:hover': { color: '#ffca28' } }}
                        >
                          <FactCheckIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Force Protection (baseline & watchlist)">
                      <IconButton
                        size="small"
                        onClick={(e) => { e.stopPropagation(); setFpDialogGeofence(geofence); }}
                        sx={{ color: colors.textSecondary, '&:hover': { color: primaryColor } }}
                      >
                        <ShieldIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <IconButton
                      size="small"
                      onClick={(e) => { e.stopPropagation(); handleToggleGeofenceVisibility(geofence.id); }}
                      sx={{ color: isSelected ? primaryColor : colors.textSecondary }}
                    >
                      {isSelected ? <VisibilityOffIcon fontSize="small" /> : <ViewIcon fontSize="small" />}
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={(e) => { e.stopPropagation(); handleDeleteGeofence(geofence.id); }}
                      sx={{ color: colors.textSecondary, '&:hover': { color: '#f44336' } }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              );
            })
          )}
        </List>
      </Paper>

      {/* Name Dialog */}
      <Dialog 
        open={nameDialogOpen} 
        onClose={handleCancelDrawing}
        PaperProps={{
          sx: {
            background: colors.cardBg,
            border: `1px solid ${colors.border}`,
            borderRadius: 3,
          }
        }}
      >
        <DialogTitle sx={{ color: colors.text }}>Name Your Geofence</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Geofence Name"
            fullWidth
            value={newGeofenceName}
            onChange={(e) => setNewGeofenceName(e.target.value)}
            disabled={isCreatingGeofence}
            sx={{
              mb: 2,
              '& .MuiInputLabel-root': { color: colors.textSecondary },
              '& .MuiOutlinedInput-root': {
                color: colors.text,
                '& fieldset': { borderColor: colors.border },
                '&:hover fieldset': { borderColor: primaryColor },
                '&.Mui-focused fieldset': { borderColor: primaryColor }
              }
            }}
          />
          <TextField
            margin="dense"
            label="Description (optional)"
            placeholder="e.g. Annual tech conference, Feb 10-12 2026, 9AM-5PM daily. Key speakers include..."
            fullWidth
            multiline
            minRows={3}
            maxRows={6}
            value={newGeofenceDescription}
            onChange={(e) => setNewGeofenceDescription(e.target.value)}
            disabled={isCreatingGeofence}
            helperText="Add context for AI analysis: event names, dates, times, purpose, expected activity, etc."
            sx={{
              '& .MuiInputLabel-root': { color: colors.textSecondary },
              '& .MuiFormHelperText-root': { color: colors.textTertiary, fontSize: '0.7rem' },
              '& .MuiOutlinedInput-root': {
                color: colors.text,
                '& fieldset': { borderColor: colors.border },
                '&:hover fieldset': { borderColor: primaryColor },
                '&.Mui-focused fieldset': { borderColor: primaryColor }
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDrawing} disabled={isCreatingGeofence} sx={{ color: colors.textSecondary }}>Cancel</Button>
          <Button
            onClick={handleSaveGeofence}
            variant="contained"
            disabled={isCreatingGeofence || !newGeofenceName.trim()}
            startIcon={isCreatingGeofence ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {isCreatingGeofence ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Activity History Dialog */}
      <Dialog 
        open={activityDialogOpen} 
        onClose={() => setActivityDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            background: colors.cardBg,
            border: `1px solid ${colors.border}`,
            borderRadius: 3,
          }
        }}
      >
        <DialogTitle sx={{ color: colors.text }}>
          Geofence Activity History
          <Typography sx={{ color: colors.textSecondary, fontSize: '0.75rem', mt: 0.5 }}>
            {activityWindowLabel}
          </Typography>
        </DialogTitle>
        <DialogContent>
          {selectedGeofenceActivity && selectedGeofenceActivity.length > 0 ? (
            <List sx={{ pt: 0 }}>
              {selectedGeofenceActivity.map((activity, idx) => (
                <ListItem 
                  key={idx}
                  sx={{
                    mb: 1,
                    borderRadius: 2,
                    border: `1px solid ${colors.border}`,
                    background: activity.currently_inside 
                      ? 'rgba(76, 175, 80, 0.1)' 
                      : isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    p: 1.5,
                  }}
                >
                  <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                    <Box>
                      <Typography sx={{ color: colors.text, fontWeight: 600, fontSize: '0.85rem' }}>
                        {activity.entity_id || activity.device_id}
                      </Typography>
                      <Typography sx={{ color: colors.textSecondary, fontSize: '0.7rem' }}>
                        {new Date(activity.enter_time).toLocaleString()}
                      </Typography>
                    </Box>
                    <Chip
                      label={activity.currently_inside ? 'Currently Inside' : activity.duration_formatted || 'Unknown duration'}
                      size="small"
                      sx={{
                        height: 22,
                        fontSize: '0.65rem',
                        fontWeight: 600,
                        backgroundColor: activity.currently_inside 
                          ? 'rgba(76, 175, 80, 0.15)' 
                          : isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
                        color: activity.currently_inside ? '#4caf50' : colors.textSecondary,
                        border: activity.currently_inside 
                          ? '1px solid rgba(76, 175, 80, 0.3)' 
                          : `1px solid ${colors.border}`,
                      }}
                    />
                  </Box>
                  {activity.exit_time && (
                    <Typography sx={{ color: colors.textTertiary, fontSize: '0.7rem' }}>
                      Left: {new Date(activity.exit_time).toLocaleString()}
                    </Typography>
                  )}
                </ListItem>
              ))}
            </List>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography sx={{ color: colors.textSecondary, fontSize: '0.85rem' }}>
                No activity in the {activityWindowLabel.toLowerCase()}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActivityDialogOpen(false)} sx={{ color: colors.textSecondary }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Force Protection Dialog */}
      <ForceProtectionDialog
        open={fpDialogGeofence !== null}
        onClose={() => setFpDialogGeofence(null)}
        geofenceId={fpDialogGeofence?.id ?? null}
        geofenceName={fpDialogGeofence?.name}
      />
      <PromoteToPacketDialog
        open={!!promoteTarget}
        onClose={() => { setPromoteTarget(null); setPromoteExtras([]); }}
        target={promoteTarget}
        extraMembers={promoteExtras}
        defaultName={promoteTarget?.label}
      />
    </Box>
  );
};

export default GeofenceTab;

export type GeofenceDrawnHandler = (geojson: any) => void;
